import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Avatar,
  Button,
  Chip,
  Rating,
  Tabs,
  Tab,
  Card,
  CardContent,

  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Switch,
  FormControlLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  FaUser,
  FaCertificate,
  FaTasks,
  FaStar,
  FaEnvelope,
  FaCog,
  FaMapMarkerAlt,
  FaPhone,
  FaGlobe,
  FaCalendar,
  FaCheck,
  FaClock,
  FaEye,
  FaTrash
} from 'react-icons/fa';
import {
  addSkill,
  removeSkill,
  updateMissionStatus,
  updateNotificationSettings,
  fetchMyProfile,
  fetchUserMissions,
  fetchUserMissionStats,
  fetchUserApplications,
  fetchUserApplicationStats,
  fetchUserMessages,
  fetchUserNotifications,
  fetchUserCommunicationStats
} from '../redux/slices/userProfileSlice';
import '../styles/Profile.css';

// Composant ProfileHeader
const ProfileHeader = ({ profile }) => {
  return (
    <Paper className="profile-header-modern" elevation={0}>
      <Box className="profile-header-background">
        <Box className="profile-header-overlay" />
      </Box>
      
      <Container className="profile-header-content">
        <Grid container spacing={3} alignItems="center">
          {/* Section Avatar */}
          <Grid item xs={12} md={3} className="profile-avatar-container">
            <Box className="profile-avatar-wrapper">
              <Avatar
                src={profile.avatar?.url}
                alt={`${profile.firstName} ${profile.lastName}`}
                className="profile-avatar-modern"
              />
              {profile.isVerified && (
                <Box className="verified-badge-modern">
                  <FaCheck className="verified-icon" />
                </Box>
              )}
              <Box className="avatar-ring" />
            </Box>
          </Grid>
          
          {/* Section Informations principales */}
          <Grid item xs={12} md={6}>
            <Box className="profile-main-info">
              <Typography variant="h3" className="profile-name-modern">
                {profile.firstName} {profile.lastName}
              </Typography>
              
              <Chip
                label={profile.type === 'professional' ? 'Professionnel du Sport' : 'Organisation Sportive'}
                className={`profile-role-chip ${profile.type}`}
                icon={<FaUser />}
              />
              
              <Box className="profile-details-modern">
                <Box className="detail-item-modern">
                  <FaMapMarkerAlt className="detail-icon-modern" />
                  <Typography variant="body1">{profile.location}</Typography>
                </Box>
                <Box className="detail-item-modern">
                  <FaCalendar className="detail-icon-modern" />
                  <Typography variant="body1">
                    Membre depuis {profile.createdAt ? new Date(profile.createdAt).toLocaleDateString('fr-FR') : 'Date inconnue'}
                  </Typography>
                </Box>
                {profile.website && (
                  <Box className="detail-item-modern">
                    <FaGlobe className="detail-icon-modern" />
                    <Typography variant="body1">{profile.website}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
          
          {/* Section Stats et Actions */}
          <Grid item xs={12} md={3}>
            <Box className="profile-stats-actions">
              <Box className="profile-stats-modern">
                <Box className="stat-card-modern">
                  <Box className="stat-rating">
                    <Rating value={profile.rating} readOnly precision={0.1} size="small" />
                    <Typography variant="h6" className="rating-value">
                      {profile.rating}/5
                    </Typography>
                  </Box>
                  <Typography variant="caption" className="rating-reviews">
                    {profile.totalReviews} avis
                  </Typography>
                </Box>
                
                <Box className="stat-card-modern">
                  <Typography variant="h5" className="missions-count">
                    {profile.completedMissions}
                  </Typography>
                  <Typography variant="caption" className="missions-label">
                    Missions terminées
                  </Typography>
                </Box>
              </Box>
              

            </Box>
          </Grid>
        </Grid>
      </Container>
    </Paper>
  );
};

// Composant SkillTags
const SkillTags = ({ skills, onAddSkill, onRemoveSkill }) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newSkill, setNewSkill] = useState({ name: '', level: 'Débutant', experience: '' });
  
  const handleAddSkill = () => {
    if (newSkill.name.trim()) {
      onAddSkill(newSkill);
      setNewSkill({ name: '', level: 'Débutant', experience: '' });
      setShowAddDialog(false);
    }
  };
  
  return (
    <Card className="skills-section">
      <CardContent>
        <Box className="section-header">
          <Typography variant="h6" className="section-title">
            <FaCertificate className="section-icon" />
            Compétences
          </Typography>

        </Box>
        
        <Box className="skills-grid">
          {skills.map((skill) => (
            <Box key={skill.id} className="skill-item">
              <Chip
                label={skill.name}
                variant="outlined"
                className={`skill-chip skill-${skill.level.toLowerCase()}`}

              />
              <Typography variant="caption" className="skill-details">
                {skill.level} • {skill.experience}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
      
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)}>
        <DialogTitle>Ajouter une compétence</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nom de la compétence"
            value={newSkill.name}
            onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            select
            label="Niveau"
            value={newSkill.level}
            onChange={(e) => setNewSkill({ ...newSkill, level: e.target.value })}
            margin="normal"
            SelectProps={{ native: true }}
          >
            <option value="Débutant">Débutant</option>
            <option value="Intermédiaire">Intermédiaire</option>
            <option value="Avancé">Avancé</option>
            <option value="Expert">Expert</option>
          </TextField>
          <TextField
            fullWidth
            label="Expérience"
            value={newSkill.experience}
            onChange={(e) => setNewSkill({ ...newSkill, experience: e.target.value })}
            margin="normal"
            placeholder="ex: 5 ans"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Annuler</Button>
          <Button onClick={handleAddSkill} variant="contained">Ajouter</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

// Composant MissionList
const MissionList = ({ missions, onUpdateStatus }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      case 'applied': return 'warning';
      default: return 'default';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Terminée';
      case 'in_progress': return 'En cours';
      case 'applied': return 'Candidature';
      default: return status;
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <FaCheck />;
      case 'in_progress': return <FaClock />;
      case 'applied': return <FaEye />;
      default: return null;
    }
  };
  
  return (
    <Card className="missions-section">
      <CardContent>
        <Typography variant="h6" className="section-title">
          <FaTasks className="section-icon" />
          Mes Missions
        </Typography>
        
        <List className="missions-list">
          {missions && missions.length > 0 ? missions.map((mission) => (
            <React.Fragment key={mission._id || mission.id}>
              <ListItem className="mission-item">
                <ListItemText
                  primary={
                    <Box className="mission-header">
                      <Typography variant="subtitle1" className="mission-title">
                        {mission.title}
                      </Typography>
                      <Chip
                        icon={getStatusIcon(mission.status)}
                        label={getStatusText(mission.status)}
                        color={getStatusColor(mission.status)}
                        size="small"
                      />
                    </Box>
                  }
                  secondary={
                    <Box className="mission-details">
                      <Typography variant="body2" color="textSecondary">
                        {mission.organization?.name || mission.organization || 'Organisation non spécifiée'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {mission.startDate && new Date(mission.startDate).toLocaleDateString('fr-FR')} - 
                        {mission.endDate && new Date(mission.endDate).toLocaleDateString('fr-FR')}
                      </Typography>
                      {mission.budget && (
                        <Typography variant="body2" className="mission-payment">
                          {mission.budget}€
                        </Typography>
                      )}
                      {mission.rating && (
                        <Rating value={mission.rating} readOnly size="small" />
                      )}
                    </Box>
                  }
                />
                <Button size="small" variant="outlined">
                  Voir détails
                </Button>
              </ListItem>
              <Divider />
            </React.Fragment>
          )) : (
            <Box p={3} textAlign="center">
              <Typography variant="body2" color="textSecondary">
                Aucune mission trouvée
              </Typography>
            </Box>
          )}
        </List>
      </CardContent>
    </Card>
  );
};

// Composant EvaluationCard
const EvaluationCard = ({ evaluations }) => {
  return (
    <Card className="evaluations-section">
      <CardContent>
        <Typography variant="h6" className="section-title">
          <FaStar className="section-icon" />
          Évaluations & Avis
        </Typography>
        
        <List className="evaluations-list">
          {evaluations.map((evaluation) => (
            <React.Fragment key={evaluation.id}>
              <ListItem className="evaluation-item">
                <ListItemText
                  primary={
                    <Box className="evaluation-header">
                      <Box>
                        <Typography variant="subtitle2">{evaluation.author}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {evaluation.authorRole}
                        </Typography>
                      </Box>
                      <Box className="evaluation-rating">
                        <Rating value={evaluation.rating} readOnly size="small" />
                        <Typography variant="caption">
                          {new Date(evaluation.date).toLocaleDateString('fr-FR')}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Box className="evaluation-content">
                      <Typography variant="body2" className="evaluation-comment">
                        "{evaluation.comment}"
                      </Typography>
                      <Typography variant="caption" color="primary">
                        Mission: {evaluation.missionTitle}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

// Composant MessagePreview


// Composant SettingsPanel
const SettingsPanel = ({ settings, onUpdateSettings }) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const handleNotificationChange = (key, value) => {
    onUpdateSettings({
      notifications: {
        ...settings.notifications,
        [key]: value
      }
    });
  };
  
  return (
    <Card className="settings-section">
      <CardContent>
        <Typography variant="h6" className="section-title">
          <FaCog className="section-icon" />
          Paramètres du compte
        </Typography>
        
        <Box className="settings-group">
          <Typography variant="subtitle1" className="settings-group-title">
            Notifications
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={settings.notifications.email}
                onChange={(e) => handleNotificationChange('email', e.target.checked)}
              />
            }
            label="Notifications par email"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.notifications.sms}
                onChange={(e) => handleNotificationChange('sms', e.target.checked)}
              />
            }
            label="Notifications par SMS"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.notifications.push}
                onChange={(e) => handleNotificationChange('push', e.target.checked)}
              />
            }
            label="Notifications push"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.notifications.newMissions}
                onChange={(e) => handleNotificationChange('newMissions', e.target.checked)}
              />
            }
            label="Nouvelles missions"
          />
        </Box>
        
        <Divider className="settings-divider" />
        
        <Box className="settings-group">
          <Typography variant="subtitle1" className="settings-group-title">
            Sécurité
          </Typography>
          <Button variant="outlined" className="settings-button">
            Modifier le mot de passe
          </Button>
        </Box>
        
        <Divider className="settings-divider" />
        
        <Box className="settings-group">
          <Typography variant="subtitle1" className="settings-group-title danger">
            Zone de danger
          </Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={<FaTrash />}
            onClick={() => setShowDeleteDialog(true)}
            className="delete-account-btn"
          >
            Supprimer mon compte
          </Button>
        </Box>
      </CardContent>
      
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Supprimer le compte</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Annuler</Button>
          <Button color="error" variant="contained">
            Supprimer définitivement
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

// Composant principal Profile
const Profile = () => {
  const dispatch = useDispatch();
  const {
    profile,
    skills,
    certifications,
    missions,
    missionStats,
    applications,
    applicationStats,
    evaluations,
    messages,
    notifications,
    communicationStats,
    settings,
    loading,
    error
  } = useSelector((state) => state.userProfile);
  
  // Récupérer l'utilisateur connecté depuis le store auth
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  
  // Logs de débogage détaillés
  console.log('=== PROFILE DEBUG COMPLET ===');
  console.log('État Redux userProfile complet:', useSelector((state) => state.userProfile));
  console.log('État Redux auth complet:', useSelector((state) => state.auth));
  console.log('profile:', profile);
  console.log('loading:', loading);
  console.log('error:', error);
  console.log('user:', user);
  console.log('isAuthenticated:', isAuthenticated);
  console.log('localStorage token:', localStorage.getItem('token'));
  console.log('localStorage user:', localStorage.getItem('user'));
  
  const [activeTab, setActiveTab] = useState(0);
  const profileDataFetched = useRef(false);

  // Récupérer les données utilisateur au chargement du composant
  useEffect(() => {
    // Éviter les appels répétés
    if (profileDataFetched.current) {
      return;
    }

    console.log('=== USEEFFECT PROFILE ===');
    console.log('Calling fetchMyProfile...');
    
    // Marquer comme appelé pour éviter les répétitions
    profileDataFetched.current = true;
    
    // Récupérer le profil de l'utilisateur connecté
    const profilePromise = dispatch(fetchMyProfile());
    
    profilePromise.then((result) => {
      console.log('fetchMyProfile result:', result);
      
      // Si on a l'utilisateur connecté, récupérer les autres données
      if (result.payload && result.payload._id) {
        const userId = result.payload._id;
        // Récupérer les missions créées et participées
        dispatch(fetchUserMissions({ userId, type: 'created' }));
        dispatch(fetchUserMissions({ userId, type: 'participated' }));
        
        // Récupérer les statistiques
        dispatch(fetchUserMissionStats(userId));
        dispatch(fetchUserApplicationStats(userId));
        dispatch(fetchUserCommunicationStats(userId));
        
        // Récupérer les candidatures
        dispatch(fetchUserApplications({ userId }));
        
        // Récupérer les messages et notifications
        dispatch(fetchUserMessages({ userId, params: { limit: 10 } }));
        dispatch(fetchUserNotifications({ userId, params: { limit: 10 } }));
      }
    }).catch((error) => {
      console.error('fetchMyProfile error:', error);
      // Réinitialiser en cas d'erreur pour permettre un nouvel essai
      profileDataFetched.current = false;
    });
  }, [dispatch]);

  // Afficher un loader si le profil est en cours de chargement
  if (loading.profile && !profile) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6">Chargement du profil...</Typography>
        </Box>
      </Container>
    );
  }

  // Afficher une erreur si le profil n'a pas pu être chargé
  if (error && !profile) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="error">
            Erreur lors du chargement du profil: {error}
          </Typography>
        </Box>
      </Container>
    );
  }

  // Afficher un indicateur de chargement pendant que le profil se charge
  if (loading.profile) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6">Chargement du profil...</Typography>
        </Box>
      </Container>
    );
  }

  // Si pas de profil et pas de chargement, afficher un message
  if (!profile && !loading.profile) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6">Aucun profil trouvé</Typography>
        </Box>
      </Container>
    );
  }
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  

  
  const handleAddSkill = (skillData) => {
    dispatch(addSkill(skillData));
  };
  
  const handleRemoveSkill = (skillId) => {
    dispatch(removeSkill(skillId));
  };
  
  const handleUpdateMissionStatus = (missionId, status) => {
    dispatch(updateMissionStatus({ missionId, status }));
  };
  

  
  const handleUpdateSettings = (newSettings) => {
    dispatch(updateNotificationSettings(newSettings));
  };
  
  const tabContent = [
    {
      label: 'Informations',
      icon: <FaUser />,
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className="personal-info-card">
              <CardContent className="personal-info-content">
                <Typography variant="h5" className="section-title-main" gutterBottom>
                  <FaUser className="section-icon-main" />
                  Informations du profil
                </Typography>
                
                <Grid container spacing={4}>
                  {/* Colonne gauche - Informations de contact */}
                  <Grid item xs={12} md={6}>
                    <Box className="info-section">
                      <Typography variant="h6" className="info-section-title">
                        Informations de contact
                      </Typography>
                      <Box className="info-grid-modern">
                        <Box className="info-item-modern">
                          <Box className="info-icon-container">
                            <FaUser className="info-icon-modern" />
                          </Box>
                          <Box className="info-content">
                            <Typography variant="body2" className="info-label-modern">Nom complet</Typography>
                            <Typography variant="body1" className="info-value-modern">{profile.firstName} {profile.lastName}</Typography>
                          </Box>
                        </Box>
                        
                        <Box className="info-item-modern">
                          <Box className="info-icon-container">
                            <FaEnvelope className="info-icon-modern" />
                          </Box>
                          <Box className="info-content">
                            <Typography variant="body2" className="info-label-modern">Email</Typography>
                            <Typography variant="body1" className="info-value-modern">{profile.email}</Typography>
                          </Box>
                        </Box>
                        
                        <Box className="info-item-modern">
                          <Box className="info-icon-container">
                            <FaPhone className="info-icon-modern" />
                          </Box>
                          <Box className="info-content">
                            <Typography variant="body2" className="info-label-modern">Téléphone</Typography>
                            <Typography variant="body1" className="info-value-modern">{profile.phone}</Typography>
                          </Box>
                        </Box>
                        
                        <Box className="info-item-modern">
                          <Box className="info-icon-container">
                            <FaMapMarkerAlt className="info-icon-modern" />
                          </Box>
                          <Box className="info-content">
                            <Typography variant="body2" className="info-label-modern">Localisation</Typography>
                            <Typography variant="body1" className="info-value-modern">{profile.location}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                  
                  {/* Colonne droite - Biographie et statistiques */}
                  <Grid item xs={12} md={6}>
                    <Box className="info-section">
                      <Typography variant="h6" className="info-section-title">
                        À propos
                      </Typography>
                      <Box className="bio-container">
                        <Typography variant="body1" className="bio-text">
                          {profile.bio}
                        </Typography>
                      </Box>
                      
                      <Box className="stats-container">
                        <Box className="stat-card">
                          <Box className="stat-icon-container">
                            <FaStar className="stat-icon" />
                          </Box>
                          <Box className="stat-content">
                            <Typography variant="h6" className="stat-value">{profile.rating}</Typography>
                            <Typography variant="body2" className="stat-label">Note moyenne</Typography>
                          </Box>
                        </Box>
                        
                        <Box className="stat-card">
                          <Box className="stat-icon-container">
                            <FaTasks className="stat-icon" />
                          </Box>
                          <Box className="stat-content">
                            <Typography variant="h6" className="stat-value">{profile.completedMissions}</Typography>
                            <Typography variant="body2" className="stat-label">Missions terminées</Typography>
                          </Box>
                        </Box>
                        
                        <Box className="stat-card">
                          <Box className="stat-icon-container">
                            <FaCalendar className="stat-icon" />
                          </Box>
                          <Box className="stat-content">
                            <Typography variant="body1" className="stat-value">{profile.createdAt ? new Date(profile.createdAt).getFullYear() : 'N/A'}</Typography>
                <Typography variant="body2" className="stat-label">Membre depuis</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Certifications
                </Typography>
                <List>
                  {certifications.map((cert) => (
                    <ListItem key={cert.id}>
                      <ListItemAvatar>
                        <Avatar className={cert.verified ? 'verified' : 'unverified'}>
                          <FaCertificate />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={cert.name}
                        secondary={`${cert.issuer} • ${new Date(cert.date).toLocaleDateString('fr-FR')}`}
                      />
                      {cert.verified && (
                        <Chip label="Vérifié" color="success" size="small" />
                      )}
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )
    },
    {
      label: 'Compétences',
      icon: <FaCertificate />,
      content: (
        <SkillTags
          skills={skills}
          onAddSkill={handleAddSkill}
          onRemoveSkill={handleRemoveSkill}

        />
      )
    },
    {
      label: 'Missions',
      icon: <FaTasks />,
      content: loading.missions ? (
        <Box display="flex" justifyContent="center" p={4}>
          <Typography>Chargement des missions...</Typography>
        </Box>
      ) : (
        <MissionList 
          missions={[...missions.created, ...missions.participated]} 
          onUpdateStatus={handleUpdateMissionStatus} 
        />
      )
    },
    {
      label: 'Évaluations',
      icon: <FaStar />,
      content: loading.stats ? (
        <Box display="flex" justifyContent="center" p={4}>
          <Typography>Chargement des évaluations...</Typography>
        </Box>
      ) : (
        <EvaluationCard evaluations={evaluations} />
      )
    },
    {
      label: 'Messages',
      icon: <FaEnvelope />,
      content: loading.messages ? (
        <Box display="flex" justifyContent="center" p={4}>
          <Typography>Chargement des messages...</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Messages récents
                </Typography>
                {messages && messages.length > 0 ? (
                  <List>
                    {messages.map((message) => (
                      <ListItem key={message._id || message.id}>
                        <ListItemText
                          primary={message.subject || message.title}
                          secondary={`De: ${message.sender?.name || message.sender} - ${new Date(message.createdAt || message.date).toLocaleDateString()}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Aucun message trouvé
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notifications récentes
                </Typography>
                {notifications && notifications.length > 0 ? (
                  <List>
                    {notifications.map((notification) => (
                      <ListItem key={notification._id || notification.id}>
                        <ListItemText
                          primary={notification.title || notification.subject}
                          secondary={`${notification.message || notification.preview} - ${new Date(notification.createdAt || notification.date).toLocaleDateString()}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Aucune notification trouvée
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )
    },
    {
      label: 'Paramètres',
      icon: <FaCog />,
      content: (
        <SettingsPanel
          settings={settings}
          onUpdateSettings={handleUpdateSettings}
        />
      )
    }
  ];
  
  return (
    <>
      <Helmet>
        <title>Mon profil - Mission Sport</title>
        <meta
          name="description"
          content="Gérez votre profil professionnel sur Mission Sport. Consultez vos missions, compétences, évaluations et paramètres de compte."
        />
        <meta property="og:title" content="Mon profil - Mission Sport" />
        <meta
          property="og:description"
          content="Gérez votre profil professionnel sur Mission Sport. Consultez vos missions, compétences, évaluations et paramètres de compte."
        />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <main className="profile-page">
        <Container maxWidth="lg" className="profile-container">
          {/* Fil d'Ariane */}
          <Box className="breadcrumb">
            <Typography variant="body2" color="textSecondary">
              Accueil {'>'} Mon profil
            </Typography>
          </Box>
          
          {/* En-tête du profil */}
          <ProfileHeader
            profile={profile}
          />
          
          {/* Navigation par onglets */}
          <Paper className="profile-tabs" elevation={1}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              className="tabs-container"
            >
              {tabContent.map((tab, index) => (
                <Tab
                  key={index}
                  label={tab.label}
                  icon={tab.icon}
                  className="profile-tab"
                />
              ))}
            </Tabs>
          </Paper>
          
          {/* Contenu des onglets */}
          <Box className="tab-content">
            {tabContent[activeTab].content}
          </Box>
        </Container>
      </main>
    </>
  );
};

export default Profile;